import React, { useEffect, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Play, Award, Trophy, CheckCircle, Clock, Star, Users, Lock, ChevronRight, FileText, Download, Video, Headphones, Image } from 'lucide-react';
import ExportBar from '../components/ExportBar';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TeacherLMS() {
  const { t, lang, currentTeacher } = useApp();
  const [courses, setCourses] = useState([]);
  const [enrolments, setEnrolments] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [tab, setTab] = useState('courses');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/lms/courses`),
      axios.get(`${API}/lms/enrolments`, { params: { teacher_id: currentTeacher?.teacher_id } }),
      axios.get(`${API}/lms/leaderboard`)
    ]).then(([c, e, l]) => {
      setCourses(c.data); setEnrolments(e.data); setLeaderboard(l.data); setLoading(false);
    }).catch(() => setLoading(false));
  }, [currentTeacher]); // eslint-disable-line react-hooks/exhaustive-deps

  const enrol = async (courseId) => {
    await axios.post(`${API}/lms/enrol`, { teacher_id: currentTeacher.teacher_id, course_id: courseId });
    toast.success(lang === 'bn' ? 'কোর্সে ভর্তি হয়েছেন' : 'Enrolled!');
    const e = await axios.get(`${API}/lms/enrolments`, { params: { teacher_id: currentTeacher?.teacher_id } });
    setEnrolments(e.data);
  };

  const submitQuiz = async () => {
    if (!selectedCourse) return;
    try {
      const res = await axios.post(`${API}/lms/quiz/submit`, {
        teacher_id: currentTeacher.teacher_id, course_id: selectedCourse.course_id, answers: quizAnswers
      });
      setQuizResult(res.data);
      toast.success(res.data.passed ? (lang === 'bn' ? 'অভিনন্দন! পাস করেছেন!' : 'Congratulations! Passed!') : (lang === 'bn' ? 'আবার চেষ্টা করুন' : 'Try again'));
    } catch (e) { toast.error('Failed'); }
  };

  const getEnrolment = (courseId) => enrolments.find(e => e.course_id === courseId);

  if (loading) return <div className="text-center py-12 text-slate-400">{t('loading')}</div>;

  const tabs = [
    { id: 'courses', label: lang === 'bn' ? 'সকল কোর্স' : 'All Courses', labelAlt: lang === 'bn' ? 'All Courses' : 'সকল কোর্স', icon: BookOpen },
    { id: 'my', label: lang === 'bn' ? 'আমার কোর্স' : 'My Courses', labelAlt: lang === 'bn' ? 'My Courses' : 'আমার কোর্স', icon: Star },
    { id: 'resources', label: lang === 'bn' ? 'রিসোর্স' : 'Resources', labelAlt: lang === 'bn' ? 'Resources' : 'রিসোর্স', icon: FileText },
    { id: 'leaderboard', label: lang === 'bn' ? 'লিডারবোর্ড' : 'Leaderboard', labelAlt: lang === 'bn' ? 'Leaderboard' : 'লিডারবোর্ড', icon: Trophy },
    { id: 'olympiad', label: lang === 'bn' ? 'গণিত অলিম্পিয়াড' : 'Math Olympiad', labelAlt: lang === 'bn' ? 'Math Olympiad' : 'গণিত অলিম্পিয়াড', icon: Award },
    { id: 'debate', label: lang === 'bn' ? 'বিতর্ক প্রতিযোগিতা' : 'Debate Competition', labelAlt: lang === 'bn' ? 'Debate Competition' : 'বিতর্ক প্রতিযোগিতা', icon: Users },
    { id: 'needs', label: lang === 'bn' ? 'প্রশিক্ষণ প্রয়োজন' : 'Need Analysis', labelAlt: lang === 'bn' ? 'Need Analysis' : 'প্রশিক্ষণ প্রয়োজন', icon: Users },
  ];

  return (
    <div className="space-y-4 animate-fade-in" data-testid="lms-page">
      <div className="card-flat p-4 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold font-bangla">{lang === 'bn' ? 'CPD ও LMS' : 'CPD & LMS — Continuous Professional Development'}</h2>
          <p className="text-xs text-slate-400">{currentTeacher?.xp_points || 0} XP | {currentTeacher?.lms_streak_days || 0} {lang === 'bn' ? 'দিনের ধারা' : 'day streak'}</p>
        </div>
        <div className="flex items-center gap-1 bg-[#C9A84C]/10 px-3 py-1.5 rounded-sm">
          <Trophy size={16} className="text-[#C9A84C]" />
          <span className="text-sm font-bold text-[#C9A84C]">{currentTeacher?.xp_points || 0}</span>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {tabs.map(tb => {
          const Icon = tb.icon;
          return (
            <button key={tb.id} data-testid={`lms-tab-${tb.id}`} onClick={() => { setTab(tb.id); setSelectedCourse(null); setQuizResult(null); }}
              className="btn btn-sm" style={{
                background: tab === tb.id ? 'var(--color-accent)' : 'var(--color-surface-2)',
                color: tab === tb.id ? 'white' : 'var(--color-label-secondary)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '8px 16px', minWidth: 90,
              }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Icon size={14} /> {tb.label}</span>
              <span style={{ fontSize: '9px', opacity: tab === tb.id ? 0.7 : 0.4 }}>{tb.labelAlt}</span>
            </button>
          );
        })}
      </div>

      {tab === 'courses' && !selectedCourse && (
        <div className="space-y-3">
          {courses.map(course => {
            const enr = getEnrolment(course.course_id);
            return (
              <div key={course.course_id} className="card-flat p-4" data-testid={`course-${course.course_id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {course.is_mandatory && <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 rounded font-bangla">{lang === 'bn' ? 'বাধ্যতামূলক' : 'Mandatory'}</span>}
                      <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">{course.accreditor}</span>
                    </div>
                    <h3 className="text-sm font-semibold font-bangla text-slate-800">{course.title_bangla}</h3>
                    <p className="text-xs text-slate-500 font-bangla mt-0.5">{course.description_bangla}</p>
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400">
                      <span className="flex items-center gap-1"><Clock size={10} />{course.duration_minutes} {lang === 'bn' ? 'মিনিট' : 'min'}</span>
                      <span className="flex items-center gap-1"><BookOpen size={10} />{course.modules?.length || 0} {lang === 'bn' ? 'মডিউল' : 'modules'}</span>
                      {course.is_offline_available && <span className="text-emerald-500">Offline</span>}
                    </div>
                  </div>
                  <div className="ml-3 flex flex-col items-end gap-2">
                    {enr ? (
                      <>
                        <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#1D9E75] rounded-full" style={{ width: `${enr.progress_percent}%` }} />
                        </div>
                        <span className="text-[10px] text-slate-400">{enr.progress_percent}%</span>
                        {enr.certificate_issued && <Award size={16} className="text-[#C9A84C]" />}
                        <button onClick={() => setSelectedCourse(course)} className="text-[10px] px-2 py-1 bg-[#1B2A4A] text-white rounded">{lang === 'bn' ? 'চালু করুন' : 'Continue'}</button>
                      </>
                    ) : (
                      <button data-testid={`enrol-${course.course_id}`} onClick={() => enrol(course.course_id)}
                        className="text-[10px] px-3 py-1.5 bg-[#1D9E75] text-white rounded">{lang === 'bn' ? 'ভর্তি হন' : 'Enrol'}</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedCourse && !quizResult && (
        <div className="space-y-3" data-testid="course-detail">
          <div className="card-flat p-4">
            <button onClick={() => setSelectedCourse(null)} className="text-xs text-slate-400 mb-2">&larr; {lang === 'bn' ? 'ফিরে যান' : 'Back'}</button>
            <h3 className="text-base font-semibold font-bangla">{selectedCourse.title_bangla}</h3>
            <div className="space-y-2 mt-3">
              {selectedCourse.modules?.map((m, i) => (
                <div key={m.module_id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-sm">
                  <div className="w-6 h-6 bg-[#1D9E75] rounded-full flex items-center justify-center text-white text-xs">{i + 1}</div>
                  <div className="flex-1">
                    <p className="text-xs font-medium font-bangla">{m.title}</p>
                    <p className="text-[10px] text-slate-400">{m.duration_min} min | {m.type}</p>
                  </div>
                  {m.type === 'video' && <Play size={14} className="text-[#1B2A4A]" />}
                </div>
              ))}
            </div>
          </div>
          {selectedCourse.quiz_questions?.length > 0 && (
            <div className="card-flat p-4">
              <h4 className="text-sm font-semibold font-bangla mb-3">{lang === 'bn' ? 'পরীক্ষা' : 'Quiz'}</h4>
              {selectedCourse.quiz_questions.map((q, i) => (
                <div key={q.id} className="mb-4">
                  <p className="text-xs font-medium font-bangla mb-2">{i + 1}. {q.question}</p>
                  <div className="space-y-1">
                    {q.options.map(opt => (
                      <label key={opt} className={`flex items-center gap-2 p-2 rounded-sm text-xs cursor-pointer transition-colors ${quizAnswers[q.id] === opt ? 'bg-[#1B2A4A] text-white' : 'bg-slate-50 hover:bg-slate-100'}`}>
                        <input type="radio" name={q.id} value={opt} checked={quizAnswers[q.id] === opt}
                          onChange={() => setQuizAnswers({ ...quizAnswers, [q.id]: opt })} className="hidden" />
                        <span className="font-bangla">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <button data-testid="submit-quiz" onClick={submitQuiz}
                className="w-full py-2.5 bg-[#1D9E75] text-white rounded-sm text-sm font-bangla">{lang === 'bn' ? 'জমা দিন' : 'Submit Quiz'}</button>
            </div>
          )}
        </div>
      )}

      {quizResult && (
        <div className="card-flat p-6 text-center" data-testid="quiz-result">
          {quizResult.passed ? <CheckCircle size={48} className="text-[#1D9E75] mx-auto mb-3" /> : <Lock size={48} className="text-red-400 mx-auto mb-3" />}
          <h3 className="text-lg font-bold font-bangla">{quizResult.passed ? (lang === 'bn' ? 'অভিনন্দন!' : 'Congratulations!') : (lang === 'bn' ? 'আবার চেষ্টা করুন' : 'Try Again')}</h3>
          <p className="text-2xl font-bold text-[#1B2A4A] mt-2">{quizResult.score}%</p>
          <p className="text-xs text-slate-500 mt-1">{quizResult.correct}/{quizResult.total} {lang === 'bn' ? 'সঠিক' : 'correct'} | +{quizResult.xp_earned} XP</p>
          <button onClick={() => { setQuizResult(null); setSelectedCourse(null); setQuizAnswers({}); }}
            className="mt-4 px-4 py-2 bg-[#1B2A4A] text-white rounded-sm text-sm">{lang === 'bn' ? 'ফিরে যান' : 'Back'}</button>
        </div>
      )}

      {tab === 'my' && (
        <div className="space-y-3">
          {enrolments.length === 0 && <p className="text-center text-sm text-slate-400 py-8">{lang === 'bn' ? 'কোনো কোর্সে ভর্তি হননি' : 'No enrolments yet'}</p>}
          {enrolments.map(enr => {
            const course = courses.find(c => c.course_id === enr.course_id);
            if (!course) return null;
            return (
              <div key={enr.enrolment_id} className="card-flat p-4 flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-semibold font-bangla">{course.title_bangla}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#1D9E75] rounded-full" style={{ width: `${enr.progress_percent}%` }} />
                    </div>
                    <span className="text-[10px] text-slate-400">{enr.progress_percent}%</span>
                  </div>
                  {enr.score && <p className="text-[10px] text-slate-400 mt-1">Score: {enr.score}% | XP: {enr.xp_earned}</p>}
                </div>
                {enr.certificate_issued ? (
                  <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4}}>
                    <div className="flex items-center gap-1" style={{color:'var(--color-warning)'}}><Award size={16} /><span style={{fontSize:'var(--text-footnote)'}}>{lang === 'bn' ? 'সনদ' : 'Cert'}</span></div>
                    <button className="btn btn-sm" style={{background:'var(--color-accent)',color:'white',padding:'3px 10px',fontSize:10}}>{lang === 'bn' ? 'ডাউনলোড' : 'Download Certificate'}</button>
                    <button className="btn btn-sm" style={{background:'var(--color-surface-2)',color:'var(--color-label-secondary)',padding:'3px 10px',fontSize:10}}>{lang === 'bn' ? 'শেয়ার' : 'Share to Community'}</button>
                  </div>
                ) : (
                  <button onClick={() => setSelectedCourse(course)} className="text-xs px-3 py-1.5 bg-[#1B2A4A] text-white rounded">{lang === 'bn' ? 'চালু' : 'Continue'}</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'resources' && (
        <div className="space-y-4" data-testid="lms-resources">
          <div style={{ marginBottom: 'var(--space-2)' }}>
            <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600, color: 'var(--color-label-primary)' }}>
              {lang === 'bn' ? 'শিক্ষা উপকরণ' : 'Teaching Resources'}
            </h3>
            <p style={{ fontSize: '11px', color: 'var(--color-label-tertiary)', marginTop: 2 }}>
              {lang === 'bn' ? 'Teaching Resources' : 'শিক্ষা উপকরণ'}
            </p>
          </div>
          {[
            { type: 'pdf', icon: FileText, color: '#C00000', title: 'NCTB National Curriculum Framework 2021', titleBn: 'NCTB জাতীয় পাঠ্যক্রম কাঠামো ২০২১', desc: 'Complete curriculum framework document for primary and secondary education', size: '4.2 MB' },
            { type: 'video', icon: Video, color: '#006A4E', title: 'Classroom Management — Best Practices', titleBn: 'শ্রেণিকক্ষ ব্যবস্থাপনা — সেরা চর্চা', desc: 'Training video on managing large classrooms effectively (45 min)', size: '320 MB' },
            { type: 'pdf', icon: FileText, color: '#C00000', title: 'Inclusive Education Guidelines (MoE)', titleBn: 'অন্তর্ভুক্তিমূলক শিক্ষা নির্দেশিকা (শিক্ষা মন্ত্রণালয়)', desc: 'Guidelines for teaching students with special needs', size: '2.1 MB' },
            { type: 'audio', icon: Headphones, color: '#b45309', title: 'Bangla Phonics Audio Pack — Grade 1', titleBn: 'বাংলা ধ্বনিবিদ্যা অডিও — শ্রেণি ১', desc: 'Audio exercises for teaching Bangla letter sounds (30 clips)', size: '85 MB' },
            { type: 'image', icon: Image, color: '#0D7C6E', title: 'Science Experiment Flashcards (Printable)', titleBn: 'বিজ্ঞান পরীক্ষা ফ্ল্যাশকার্ড (প্রিন্টযোগ্য)', desc: '25 printable flashcards for Grade 3-5 science experiments', size: '12 MB' },
            { type: 'pdf', icon: FileText, color: '#C00000', title: 'Teacher Evaluation Rubric 2026', titleBn: 'শিক্ষক মূল্যায়ন রুব্রিক ২০২৬', desc: 'Official performance evaluation criteria for teachers', size: '1.5 MB' },
            { type: 'video', icon: Video, color: '#006A4E', title: 'Using Shiksha BD Tablet — Beginner Guide', titleBn: 'শিক্ষা বিডি ট্যাবলেট ব্যবহার — শুরুর নির্দেশিকা', desc: 'Step-by-step tutorial for new teachers (20 min)', size: '180 MB' },
            { type: 'pdf', icon: FileText, color: '#C00000', title: 'Mental Health First Aid for Teachers', titleBn: 'শিক্ষকদের জন্য মানসিক স্বাস্থ্য প্রাথমিক চিকিৎসা', desc: 'Recognizing and responding to mental health concerns in schools', size: '3.8 MB' },
            { type: 'pdf', icon: FileText, color: '#C00000', title: 'Mathematics Teaching with Manipulatives', titleBn: 'হাতে-কলমে গণিত শিক্ষাদান', desc: 'Guide to using physical objects for teaching math concepts', size: '5.6 MB' },
            { type: 'video', icon: Video, color: '#006A4E', title: 'Formative Assessment Techniques', titleBn: 'ধারাবাহিক মূল্যায়ন কৌশল', desc: 'Video workshop on continuous assessment methods (35 min)', size: '250 MB' },
          ].map((r, i) => {
            const Icon = r.icon;
            return (
              <div key={i} className="card-surface" style={{ padding: 'var(--space-4) var(--space-5)', display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-md)', background: `${r.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={20} style={{ color: r.color }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-primary)' }}>
                    {lang === 'bn' ? r.titleBn : r.title}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--color-label-tertiary)', marginTop: 1 }}>
                    {lang === 'bn' ? r.title : r.titleBn}
                  </div>
                  <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-secondary)', marginTop: 4 }}>{r.desc}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                  <span className="badge badge-neutral" style={{ textTransform: 'uppercase' }}>{r.type}</span>
                  <span style={{ fontSize: '10px', color: 'var(--color-label-tertiary)' }}>{r.size}</span>
                  <button className="btn btn-sm" style={{ background: 'var(--color-accent)', color: 'white', padding: '4px 12px', fontSize: 11 }}>
                    <Download size={12} /> {lang === 'bn' ? 'ডাউনলোড' : 'Download'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MATH OLYMPIAD */}
      {tab === 'olympiad' && (
        <div className="space-y-4" data-testid="lms-olympiad">
          <div className="card-surface" style={{ borderTop: '3px solid var(--color-red)' }}>
            <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{lang === 'bn' ? 'জাতীয় গণিত অলিম্পিয়াড ২০২৬' : 'National Mathematics Olympiad 2026'}</h3>
            <div style={{ fontSize: 11, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'National Mathematics Olympiad 2026' : 'জাতীয় গণিত অলিম্পিয়াড ২০২৬'}</div>
            <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-2)', lineHeight: 1.6 }}>{lang === 'bn' ? 'বিদ্যালয় → উপজেলা → জেলা → বিভাগীয় → জাতীয় পর্যায় প্রতিযোগিতা' : 'School → Upazila → District → Divisional → National level competitions'}</p>
          </div>
          {[
            { round: 'School Round', roundBn: 'বিদ্যালয় পর্যায়', date: '2026-04-20', status: 'upcoming', venue: 'Respective Schools', participants: 2, eligible: 'Grade 3-12' },
            { round: 'Upazila Round', roundBn: 'উপজেলা পর্যায়', date: '2026-05-15', status: 'upcoming', venue: 'Upazila Education Office', participants: 0, eligible: 'School Round Winners' },
            { round: 'District Round', roundBn: 'জেলা পর্যায়', date: '2026-06-15', status: 'upcoming', venue: 'District Education Office', participants: 0, eligible: 'Upazila Winners' },
            { round: 'Divisional Round', roundBn: 'বিভাগীয় পর্যায়', date: '2026-08-10', status: 'upcoming', venue: 'Divisional HQ', participants: 0, eligible: 'District Winners' },
            { round: 'National Final', roundBn: 'জাতীয় ফাইনাল', date: '2026-10-01', status: 'upcoming', venue: 'Bangabandhu Conference Centre, Dhaka', participants: 0, eligible: 'Divisional Winners' },
          ].map((r, i) => (
            <div key={i} className="card-surface" style={{ padding: 'var(--space-4) var(--space-5)', display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: r.participants > 0 ? 'var(--color-accent-light)' : 'var(--color-surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: r.participants > 0 ? 'var(--color-accent)' : 'var(--color-label-tertiary)' }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{lang === 'bn' ? r.roundBn : r.round}</div>
                <div style={{ fontSize: 10, color: 'var(--color-label-tertiary)', marginTop: 1 }}>{lang === 'bn' ? r.round : r.roundBn}</div>
                <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-secondary)', marginTop: 4 }}>{r.date} | {r.venue} | {lang === 'bn' ? 'যোগ্যতা' : 'Eligible'}: {r.eligible}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span className={"badge " + (r.status === 'upcoming' ? 'badge-accent' : 'badge-success')}>{r.status}</span>
                {r.participants > 0 && <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-accent)', marginTop: 4 }}>{r.participants} {lang === 'bn' ? 'জন নিবন্ধিত' : 'registered'}</div>}
              </div>
            </div>
          ))}
          <button className="btn" style={{ background: 'var(--color-accent)', color: 'white', width: '100%' }}>{lang === 'bn' ? 'শিক্ষার্থী নিবন্ধন করুন' : 'Register Students for School Round'}</button>
        </div>
      )}

      {/* DEBATE COMPETITION */}
      {tab === 'debate' && (
        <div className="space-y-4" data-testid="lms-debate">
          <div className="card-surface" style={{ borderTop: '3px solid var(--color-accent)' }}>
            <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{lang === 'bn' ? 'বাধ্যতামূলক বিতর্ক প্রতিযোগিতা ও ক্লাব' : 'Compulsory Debate Competition & Clubs'}</h3>
            <div style={{ fontSize: 11, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Debate Competition & Clubs' : 'বিতর্ক প্রতিযোগিতা ও ক্লাব'}</div>
            <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-2)', lineHeight: 1.6 }}>{lang === 'bn' ? 'প্রতিটি প্রতিষ্ঠানে বিতর্ক ক্লাব প্রতিষ্ঠা এবং বাধ্যতামূলক বিতর্ক প্রতিযোগিতা' : 'Establish debate clubs in every institution with compulsory debate competitions'}</p>
          </div>
          <div className="card-surface">
            <h4 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, marginBottom: 'var(--space-3)' }}>{lang === 'bn' ? 'বিদ্যালয় বিতর্ক ক্লাব' : 'School Debate Clubs'}<span style={{ display: 'block', fontSize: 10, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Debate Clubs' : 'বিতর্ক ক্লাব'}</span></h4>
            {[
              { school: 'Dhaka Model Govt. Primary School', members: 25, advisor: 'Fatema Begum', status: 'active' },
              { school: 'Mirpur Govt. Girls High School', members: 32, advisor: 'Kamrul Hasan', status: 'active' },
              { school: 'Sylhet Central Govt. Primary School', members: 18, advisor: 'Nasrin Akter', status: 'active' },
              { school: 'Chittagong City Govt. Secondary School', members: 28, advisor: 'Shamsul Alam', status: 'active' },
              { school: 'Patuakhali Rural Primary School', members: 12, advisor: 'Rubina Khatun', status: 'active' },
            ].map((club, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3) 0', borderBottom: i < 4 ? '1px solid var(--color-separator)' : 'none' }}>
                <div style={{ flex: 1 }}><div style={{ fontSize: 'var(--text-subhead)', fontWeight: 500 }}>{club.school}</div>
                  <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'উপদেষ্টা' : 'Advisor'}: {club.advisor} | {club.members} {lang === 'bn' ? 'জন সদস্য' : 'members'}</div></div>
                <span className="badge badge-success">{club.status}</span>
              </div>
            ))}
          </div>
          <div className="card-surface">
            <h4 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, marginBottom: 'var(--space-3)' }}>{lang === 'bn' ? 'আসন্ন প্রতিযোগিতা' : 'Upcoming Competitions'}</h4>
            <div className="card-surface" style={{ borderLeft: '3px solid var(--color-accent)', padding: 'var(--space-4)' }}>
              <div style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{lang === 'bn' ? 'আন্তঃবিদ্যালয় বিতর্ক চ্যাম্পিয়নশিপ ২০২৬' : 'Inter-School Debate Championship 2026'}</div>
              <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-secondary)', marginTop: 4 }}>2026-05-10 | {lang === 'bn' ? 'বিষয়' : 'Topic'}: "{lang === 'bn' ? 'AI কি শিক্ষকদের প্রতিস্থাপন করবে?' : 'Will AI Replace Teachers?'}"</div>
              <button className="btn btn-sm" style={{ background: 'var(--color-accent)', color: 'white', marginTop: 'var(--space-3)' }}>{lang === 'bn' ? 'দল নিবন্ধন করুন' : 'Register Team'}</button>
            </div>
          </div>
        </div>
      )}

      {tab === 'needs' && (
        <div className="space-y-4" data-testid="lms-needs">
          <div className="card-surface" style={{padding:'var(--space-5)'}}>
            <h3 style={{fontSize:'var(--text-body)',fontWeight:600,marginBottom:'var(--space-3)'}}>{lang === 'bn' ? 'প্রশিক্ষণ প্রয়োজনীয়তা বিশ্লেষণ' : 'Training Need Analysis'}</h3>
            <p style={{fontSize:'11px',color:'var(--color-label-tertiary)',marginTop:2}}>{lang === 'bn' ? 'Training Need Analysis' : 'প্রশিক্ষণ প্রয়োজনীয়তা বিশ্লেষণ'}</p>
            <p style={{fontSize:'var(--text-subhead)',color:'var(--color-label-secondary)',marginTop:'var(--space-2)',lineHeight:1.6}}>{lang === 'bn' ? 'আপনি কোন ধরনের প্রশিক্ষণ চান তা জানান। আপনার পরামর্শ NAEM-এ পাঠানো হবে।' : 'Suggest the training you need. Your suggestions will be sent to NAEM.'}</p>
          </div>
          <div className="card-surface" style={{padding:'var(--space-5)'}}>
            <h4 style={{fontSize:'var(--text-subhead)',fontWeight:600,marginBottom:'var(--space-3)'}}>{lang === 'bn' ? '৫টি CPD ডোমেইন' : '5 CPD Domains'}</h4>
            {[{en:'Policy & NAEM Mandated',bn:'নীতি ও NAEM বাধ্যতামূলক',desc:'NAEM minimum training hours, government policies'},{en:'School Management',bn:'বিদ্যালয় ব্যবস্থাপনা',desc:'Administration, planning, resource management'},{en:'Classroom Management',bn:'শ্রেণিকক্ষ ব্যবস্থাপনা',desc:'Teaching techniques, student engagement, discipline'},{en:'Subject Pedagogy',bn:'বিষয়ভিত্তিক শিক্ষাবিদ্যা',desc:'Subject-specific teaching methods and strategies'},{en:'Technology & Innovation',bn:'প্রযুক্তি ও উদ্ভাবন',desc:'Digital literacy, EdTech tools, AI in education'}].map((d,i) => (
              <div key={i} className="list-row" style={{borderRadius:'var(--radius-sm)',padding:'var(--space-3) var(--space-4)',marginBottom:'var(--space-2)'}}>
                <div className="list-row-icon" style={{background:i<2?'var(--color-error-bg)':'var(--color-accent-light)'}}><span style={{fontSize:14,fontWeight:700,color:i<2?'var(--color-error)':'var(--color-accent)'}}>{i+1}</span></div>
                <div style={{flex:1}}><div style={{fontSize:'var(--text-subhead)',fontWeight:500}}>{lang === 'bn' ? d.bn : d.en}</div><div style={{fontSize:'10px',color:'var(--color-label-tertiary)',marginTop:1}}>{lang === 'bn' ? d.en : d.bn}</div><div style={{fontSize:'var(--text-footnote)',color:'var(--color-label-secondary)',marginTop:2}}>{d.desc}</div></div>
                {i<2 && <span className="badge badge-error" style={{fontSize:9}}>MANDATORY</span>}
              </div>
            ))}
          </div>
          <div className="card-surface" style={{padding:'var(--space-5)'}}>
            <h4 style={{fontSize:'var(--text-subhead)',fontWeight:600,marginBottom:'var(--space-3)'}}>{lang === 'bn' ? 'প্রশিক্ষণ পরামর্শ দিন' : 'Suggest Training'}</h4>
            <textarea className="input-filled" rows={3} placeholder={lang === 'bn' ? 'আপনি কোন বিষয়ে প্রশিক্ষণ চান তা লিখুন...' : 'Describe the training you need...'}></textarea>
            <button className="btn btn-sm" style={{background:'var(--color-accent)',color:'white',marginTop:'var(--space-3)'}}>{lang === 'bn' ? 'জমা দিন' : 'Submit Suggestion'}</button>
          </div>
        </div>
      )}

      {tab === 'leaderboard' && (
        <div className="card-flat p-4">
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:'var(--space-3)'}}>
            <ExportBar title="CPD Leaderboard" data={leaderboard} columns={[
              {label:'Name',accessor:r=>r.name_bangla},{label:'XP Points',accessor:'xp_points'},
              {label:'Streak Days',accessor:'lms_streak_days'},{label:'School',accessor:'school_name'}
            ]} />
          </div>
          <div className="space-y-2">
            {leaderboard.map((t, i) => (
              <div key={t.teacher_id} className={`flex items-center gap-3 p-2 rounded-sm ${i < 3 ? 'bg-[#C9A84C]/5' : ''}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-[#C9A84C] text-white' : i === 1 ? 'bg-slate-300 text-white' : i === 2 ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-500'}`}>{i + 1}</span>
                <div className="flex-1">
                  <p className="text-xs font-medium font-bangla">{t.name_bangla}</p>
                  <p className="text-[10px] text-slate-400">{t.school_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-[#C9A84C]">{t.xp_points} XP</p>
                  <p className="text-[10px] text-slate-400">{t.lms_streak_days}d streak</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AdminLMS() {
  const { lang } = useApp();
  const [courses, setCourses] = useState([]);
  const [enrolments, setEnrolments] = useState([]);
  useEffect(() => {
    Promise.all([axios.get(`${API}/lms/courses`), axios.get(`${API}/lms/enrolments`)]).then(([c, e]) => { setCourses(c.data); setEnrolments(e.data); });
  }, []);

  const completionData = courses.map(c => {
    const ce = enrolments.filter(e => e.course_id === c.course_id);
    const completed = ce.filter(e => e.completed_at).length;
    return { name: c.title_bangla?.substring(0, 15) + '...', enrolled: ce.length, completed };
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-lms">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="kpi-card"><p className="kpi-value text-[#1B2A4A]">{courses.length}</p><p className="kpi-label">{lang === 'bn' ? 'মোট কোর্স' : 'Total Courses'}</p></div>
        <div className="kpi-card"><p className="kpi-value text-[#1D9E75]">{enrolments.length}</p><p className="kpi-label">{lang === 'bn' ? 'মোট ভর্তি' : 'Total Enrolments'}</p></div>
        <div className="kpi-card"><p className="kpi-value text-[#C9A84C]">{enrolments.filter(e => e.completed_at).length}</p><p className="kpi-label">{lang === 'bn' ? 'সম্পন্ন' : 'Completions'}</p></div>
        <div className="kpi-card"><p className="kpi-value text-[#1B2A4A]">{enrolments.filter(e => e.certificate_issued).length}</p><p className="kpi-label">{lang === 'bn' ? 'সনদ প্রদান' : 'Certificates Issued'}</p></div>
      </div>
      <div className="card-flat p-6">
        <h3 className="text-sm font-semibold mb-4">{lang === 'bn' ? 'কোর্স সম্পন্ন বিশ্লেষণ' : 'Course Completion Analytics'}</h3>
        <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={200}>
          <BarChart data={completionData}>
            <XAxis dataKey="name" tick={{ fontSize: 9 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="enrolled" fill="#1B2A4A" name="Enrolled" />
            <Bar dataKey="completed" fill="#1D9E75" name="Completed" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function LMS() {
  const { isTeacher } = useApp();
  return isTeacher ? <TeacherLMS /> : <AdminLMS />;
}
